import useProjectSafety from "../../hooks/ProjectSafety.hook";
import PropDeletionBlockedDialog from "../prop_presets/PropDeletionBlockedDialog";
import type { PropReference } from "../../helpers/PropReferences";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import {
  addEnvironmentPropPresetToAllTakes,
  deletePropPreset,
  selectEnvironmentPropMetadataIds,
  selectEnvironmentPropPresetsByMetadataId,
} from "../../app/slices/TrainingSlice";
import { addUsedProps } from "../../app/slices/PropLibrarySlice";
import { PropType } from "../../models/Prop";
import {
  loadEnvironmentPropRenderFailure,
  loadEnvironmentPropRenderStart,
  loadEnvironmentPropRenderSuccess,
  selectEnvironmentPropDetailsByPropId,
  selectEnvironmentPropPlacements,
  selectEnvironmentPropRenderStatusByPropId,
  selectEnvironmentPropRenderUrisByPropId,
} from "../../app/slices/EnvironmentPropSlice";
import { selectShowEnvironmentProps } from "../../app/slices/GlobalSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { EnvironmentPropDTO } from "../../dto/EnvironmentPropDTO";
import { PropDetailsDTO } from "../../dto/PropDetailsDTO";
import { PropMetadata } from "../../dto/PropMetadata";
import { TranslatedPropContentDTO } from "../../dto/TranslatedPropContentDTO";
import { UsedProp } from "../../models/UsedProp";
import { PropService } from "../../services/PropService";
import InteractionPointPreview, {
  PropPointStrategy,
} from "./InteractionPointPreview";

interface EnvironmentPropMarker {
  placement: EnvironmentPropDTO;
  details?: PropDetailsDTO;
  renderUri?: string;
}

const DEFAULT_PROP_TYPE = 0;
const CREATOR_PRIMARY_COLOR = { r: 58 / 255, g: 196 / 255, b: 1, a: 1 };
const CORRECT_GREEN = { r: 143 / 255, g: 188 / 255, b: 143 / 255, a: 1 };

function toPropContent(marker: EnvironmentPropMarker): TranslatedPropContentDTO {
  const details = marker.details;
  return {
    id: marker.placement.propId,
    name: details?.name ?? marker.placement.propId,
    downloadUri: marker.renderUri ?? "",
    categories: [],
    metadataUri: details?.metadataUri ?? "",
    interactions: [],
    hideInPropLibrary: details?.hideInPropLibrary ?? false,
  };
}

function toPreviewUsedProp(marker: EnvironmentPropMarker): UsedProp {
  const details = marker.details;
  const metadata: PropMetadata = {
    id: marker.placement.propId,
    name: details?.name ?? marker.placement.propId,
    prefab_name: "",
    prop_states: [],
    clickableTags: [],
    inspectDefinitions: [],
    attachmentTags: [{ tag: "" }],
    categories: details?.categories ?? [],
    hideInPropLibrary: details?.hideInPropLibrary ?? false,
  };

  return { propContent: toPropContent(marker), metadata };
}

function toUsedProp(
  marker: EnvironmentPropMarker,
  metadata: PropMetadata
): UsedProp {
  return { propContent: toPropContent(marker), metadata };
}

export default function Viewport3DEnvironmentPropLayer() {
  const { getPropReferencesInProject } = useProjectSafety();
  const [blockedReferences, setBlockedReferences] = useState<PropReference[]>([]);
  const dispatch = useDispatch();
  const currentOrganizationId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);
  const showEnvironmentProps = useSelector(selectShowEnvironmentProps);
  const placements = useSelector(selectEnvironmentPropPlacements);
  const detailsByPropId = useSelector(selectEnvironmentPropDetailsByPropId);
  const renderUrisByPropId = useSelector(selectEnvironmentPropRenderUrisByPropId);
  const renderStatusByPropId = useSelector(
    selectEnvironmentPropRenderStatusByPropId
  );
  const selectedEnvironmentMetadataIds = useSelector(
    selectEnvironmentPropMetadataIds
  );
  const selectedEnvironmentPropPresetsByMetadataId = useSelector(
    selectEnvironmentPropPresetsByMetadataId
  );
  const selectedEnvironmentMetadataIdSet = useMemo(
    () => new Set(selectedEnvironmentMetadataIds),
    [selectedEnvironmentMetadataIds]
  );

  const markers = useMemo(
    () =>
      placements
        .filter(
          (placement) =>
            showEnvironmentProps ||
            selectedEnvironmentMetadataIdSet.has(placement.environmentMetadataId)
        )
        .map((placement) => ({
          placement,
          details: detailsByPropId[placement.propId],
          renderUri: renderUrisByPropId[placement.propId],
        })),
    [
      detailsByPropId,
      placements,
      renderUrisByPropId,
      selectedEnvironmentMetadataIdSet,
      showEnvironmentProps,
    ]
  );

  useEffect(() => {
    if (markers.length === 0 || !currentOrganizationId || !token) {
      return;
    }

    markers.forEach(({ placement }) => {
      const propId = placement.propId;
      if (renderUrisByPropId[propId] || renderStatusByPropId[propId]) return;

      dispatch(loadEnvironmentPropRenderStart({ propId }));
      PropService.GetPropRenderThumbnail(
        currentOrganizationId,
        token,
        DEFAULT_PROP_TYPE,
        propId,
        propId
      ).then((render) => {
        if (render?.downloadUri) {
          dispatch(
            loadEnvironmentPropRenderSuccess({
              propId,
              downloadUri: render.downloadUri,
            })
          );
          return;
        }
        dispatch(loadEnvironmentPropRenderFailure({ propId }));
      });
    });
  }, [
    currentOrganizationId,
    dispatch,
    markers,
    renderStatusByPropId,
    renderUrisByPropId,
    token,
  ]);

  const markerViews = useMemo(
    () =>
      markers.map((marker) => {
        const environmentMetadataId = marker.placement.environmentMetadataId;
        const isSelected =
          selectedEnvironmentMetadataIdSet.has(environmentMetadataId);
        const selectedPreset =
          selectedEnvironmentPropPresetsByMetadataId[environmentMetadataId];
        const showSelectedStyle = showEnvironmentProps && isSelected;
        const previewProp = toPreviewUsedProp(marker);

        return (
          <InteractionPointPreview
            key={`${environmentMetadataId}:${marker.placement.propId}`}
            strategy={PropPointStrategy}
            x={marker.placement.position.x}
            y={marker.placement.position.y}
            prop={previewProp}
            directionDegrees={marker.placement.direction?.x}
            directionColor={showSelectedStyle ? CORRECT_GREEN : CREATOR_PRIMARY_COLOR}
            markerBorderColor={showSelectedStyle ? CORRECT_GREEN : undefined}
            markerIconColor={showSelectedStyle ? CORRECT_GREEN : undefined}
            showCheckBadge={showSelectedStyle}
            onClick={async () => {
              if (showEnvironmentProps && selectedPreset) {
                const references = getPropReferencesInProject(selectedPreset);
                if (references.length) setBlockedReferences(references);
                else dispatch(deletePropPreset(selectedPreset.id));
                return;
              }
              if (isSelected) return;

              const metadataUri = marker.details?.metadataUri;
              if (!metadataUri) return;

              const metadata = await PropService.GetPropMetadata(metadataUri);
              if (!metadata?.prefab_name) return;
              const enrichedMetadata = {
                ...metadata,
                hideInPropLibrary: marker.details?.hideInPropLibrary ?? false,
              };

              const usedProp = toUsedProp(marker, enrichedMetadata);
              dispatch(addUsedProps([usedProp]));
              dispatch(
                addEnvironmentPropPresetToAllTakes({
                  id: environmentMetadataId,
                  propId: marker.placement.propId,
                  name: marker.details?.name ?? marker.placement.propId,
                  prefabName: enrichedMetadata.prefab_name,
                  propType: PropType.Environment,
                  hideInPropLibrary: marker.details?.hideInPropLibrary ?? false,
                  environmentMetadataId,
                })
              );
            }}
          />
        );
      }),
    [
      dispatch,
      markers,
      getPropReferencesInProject,
      selectedEnvironmentMetadataIdSet,
      selectedEnvironmentPropPresetsByMetadataId,
      showEnvironmentProps,
    ]
  );

  return (
    <>
      {markerViews}
      <PropDeletionBlockedDialog
        references={blockedReferences}
        onClose={() => setBlockedReferences([])}
      />
    </>
  );
}
